import React, { useState, useEffect, useCallback } from 'react';
import MerchantTab from '../components/MerchantTab';
import InvoiceTab from '../components/InvoiceTab';
import PaymentPlansTab from '../components/PaymentPlansTab';
import NotificationsTab from '../components/NotificationsTab';
import TransactionsTab from '../components/TransactionsTab';
import ClaimsTab from '../components/ClaimsTab';
import LedgerTab from '../components/LedgerTab';
import ARAgingTab from '../components/ARAgingTab';
import ScheduleTab from '../components/ScheduleTab';
import GeneralReportsTab from '../components/GeneralReportsTab';
import ReportTab from '../components/ReportTab';
import PaymentReportTab from '../components/PaymentReportTab';
import DepositReportTab from '../components/DepositReportTab';
import RefundDetailsTab from '../components/RefundDetailsTab';
import ChargebackDetailsTab from '../components/ChargebackDetailsTab';
import DeviceManagementTab from '../components/DeviceManagementTab';
import SettingsTab from '../components/SettingsTab';
import RemindersTab from '../components/RemindersTab';
import KollaSyncIndicator from '../components/KollaSyncIndicator';
import PracticePicker from '../components/PracticePicker';
import Icon from '../components/Icon';
import { useHidePhi } from '../lib/phiPrivacy';
import newtonWordmark from '../assets/newton-wordmark.svg';
import { IS_SANDBOX_MODE } from '../services/common';
import { getMerchantInfo } from '../services/paymentsService';
import {
  persistPracticeSelection,
  pickLoginPracticeId,
  readSelectablePractices,
  loadEnabledPractices,
} from '../lib/practiceAccess';

// Page header copy for every tab id — including the ones currently hidden
// from the sidebar (transactions / claims / settings), which are still
// reachable by ?tab= deep link and would otherwise render a blank title.
const TAB_META = {
  ledger: { title: 'Ledger', description: 'Patient balances, transactions and claims from your practice management system.' },
  'ar-aging': { title: 'AR Aging', description: 'Outstanding patient and insurance balances, grouped by age.' },
  report: { title: 'Report', description: 'Daily, weekly and monthly production, collections, hygiene and front-office performance from your practice management system.' },
  invoice: { title: 'Invoices', description: 'Create, send and collect on patient invoices.' },
  schedule: { title: 'Schedule', description: 'Your appointment book, colored by what each visit still owes once insurance has paid.' },
  'payment-plans': { title: 'Payment Plans', description: 'Recurring payment schedules and upcoming charges.' },
  notifications: { title: 'Notifications', description: 'Declined and interrupted payments that need follow-up.' },
  merchant: { title: 'Merchant', description: 'Your merchant account and onboarding status.' },
  reminders: { title: 'Reminders', description: 'Automated text reminders for unpaid invoices.' },
  transactions: { title: 'Transactions', description: 'Payment transactions recorded for this practice.' },
  claims: { title: 'Claims', description: 'Insurance claims and their status.' },
  reconciliation: { title: 'Reconciliation', description: 'Every payment with its surcharge, processing fee and net to the practice, to tie out against bank deposits and the PMS.' },
  'payment-report': { title: 'Payment Report', description: 'Every payment processed for this practice.' },
  'deposit-report': { title: 'Deposit Report', description: 'Deposits settled to your bank account.' },
  'refund-details': { title: 'Refund Details', description: 'Refunds issued and their status.' },
  'chargeback-details': { title: 'Chargeback Details', description: 'Disputed payments and chargeback cases.' },
  'device-management': { title: 'Device Management', description: 'Card terminals registered to this practice.' },
  settings: { title: 'Settings', description: 'Payment preferences for this practice.' },
};

const Dashboard = () => {
  const [phiHidden, setPhiHidden] = useHidePhi();
  const isDemo = new URLSearchParams(window.location.search).get('demo') === 'true';

  // Load Rainforest JS bundles. Loads sandbox.* bundles when either
  // (a) the global IS_SANDBOX_MODE switch is on (mirrors the backend's
  // RAINFOREST_BASE_URL), or (b) the dashboard was opened with ?demo=true
  // (one-off override that still works in production mode).
  useEffect(() => {
    const prefix = (IS_SANDBOX_MODE || isDemo) ? 'sandbox.' : '';
    const scripts = [`${prefix}merchant.js`, `${prefix}payment.js`];
    const loaded = [];

    scripts.forEach((name) => {
      const id = `rainforest-${name}`;
      if (!document.getElementById(id)) {
        const el = document.createElement('script');
        el.id = id;
        el.type = 'module';
        el.src = `https://static.rainforestpay.com/${name}`;
        document.head.appendChild(el);
        loaded.push(el);
      }
    });

    return () => loaded.forEach((el) => el.remove());
  }, [isDemo]);
  const [practices, setPractices] = useState(readSelectablePractices);
  // True once the fresh enabled-practices list (with feature flags) is in.
  const [practicesLoaded, setPracticesLoaded] = useState(false);
  const [practiceId, setPracticeId] = useState(() =>
    pickLoginPracticeId(readSelectablePractices(), { practiceId: localStorage.getItem('practiceId') })
  );
  const userEmail = localStorage.getItem('useremail');

  // Track whether the merchant is fully ACTIVE on Rainforest. Practices
  // that are missing a merchant record entirely, or stuck mid-onboarding
  // (PENDING / ONBOARDING / SUSPENDED / DEACTIVATED / CANCELED, or no
  // merchant doc at all), need to land on the Merchant tab so they can
  // finish setup before doing anything else.
  //
  // We seed from the ``merchantStatus`` localStorage value primed at
  // login so the sidebar can render the right ordering on first paint
  // without waiting on an API round-trip. A background refresh below
  // keeps the cache in sync (and MerchantTab also writes through to it
  // on every load), so the cache is self-healing once a practice
  // finishes onboarding.
  const readCachedMerchantActive = () => {
    const cached = localStorage.getItem('merchantStatus');
    if (!cached) return null; // unknown — first load before login completed the prime
    return cached === 'ACTIVE';
  };
  const [isMerchantActive, setIsMerchantActive] = useState(readCachedMerchantActive);

  const handlePracticeChange = useCallback((nextId) => {
    if (!nextId || nextId === practiceId) return;
    localStorage.setItem('practiceId', nextId);
    localStorage.removeItem('merchantStatus');
    setIsMerchantActive(null);
    setPracticeId(nextId);
    persistPracticeSelection(nextId);
  }, [practiceId]);

  useEffect(() => {
    if (practiceId && localStorage.getItem('practiceId') !== practiceId) {
      localStorage.setItem('practiceId', practiceId);
    }
  }, [practiceId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const enabled = await loadEnabledPractices();
        if (cancelled) return;
        setPractices(enabled);
        setPracticesLoaded(true);
        setPracticeId((current) => {
          if (current && enabled.some((p) => p.practiceId === current)) return current;
          const next = pickLoginPracticeId(enabled, { practiceId: current });
          if (next) localStorage.setItem('practiceId', next);
          return next;
        });
      } catch (err) {
        console.warn('Failed to load payments-enabled practices:', err);
        if (!cancelled) setPracticesLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!practiceId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getMerchantInfo(practiceId);
        if (cancelled) return;
        const status = data?.merchant_status === 'found'
          ? data?.merchant?.merchant_status
          : data?.merchant_status; // 'not_found' or legacy shape
        if (status) {
          localStorage.setItem('merchantStatus', status);
        } else {
          localStorage.removeItem('merchantStatus');
        }
        setIsMerchantActive(status === 'ACTIVE');
      } catch (err) {
        if (!cancelled && isMerchantActive === null) setIsMerchantActive(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceId]);

  // Get initial tab from URL
  const getInitialTab = () => {
    const urlParams = new URLSearchParams(window.location.search);
    // ?tab=general-reports was the Report tab's first id; keep old links working.
    // Retired ids for the Report tab -> its current id, so old links keep working.
    const LEGACY_TAB_IDS = { 'general-reports': 'report', reports: 'report' };
    const rawTab = urlParams.get('tab');
    const tabFromUrl = LEGACY_TAB_IDS[rawTab] || rawTab;
    const validTabs = ['merchant', 'invoice', 'schedule', 'payment-plans', 'notifications', 'ledger', 'ar-aging', 'report', 'transactions', 'claims', 'reconciliation', 'payment-report', 'deposit-report', 'refund-details', 'chargeback-details', 'device-management', 'reminders', 'settings'];

    if (tabFromUrl && validTabs.includes(tabFromUrl)) {
      return tabFromUrl;
    }
    return null; // resolved after merchant lookup completes
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);

  // Cross-tab "Create & Charge" handoff. When the Ledger preview creates an
  // invoice and wants to charge it immediately, it parks the new invoice
  // here and we switch to the Invoices tab, which opens the terminal/device
  // charge modal for it (then clears this back to null).
  const [chargeRequest, setChargeRequest] = useState(null);

  const requestCharge = useCallback((invoice) => {
    if (!invoice) return;
    setChargeRequest(invoice);
    setActiveTab('invoice');
  }, []);

  // Once we know the merchant status, pick a sensible default tab if the
  // URL didn't specify one. Non-ACTIVE merchants land on Merchant so they
  // immediately see the onboarding/underwriting checklist; ACTIVE
  // merchants land on Ledger like before.
  useEffect(() => {
    if (activeTab !== null) return;
    if (isMerchantActive === null) return; // still loading
    setActiveTab(isMerchantActive ? 'ledger' : 'merchant');
  }, [activeTab, isMerchantActive]);
  
  // Persist tab selection to URL
  useEffect(() => {
    if (!activeTab) return;
    const url = new URL(window.location);
    url.searchParams.set('tab', activeTab);
    window.history.replaceState({}, '', url);
  }, [activeTab]);

  // Sidebar order: when the merchant is missing or in any non-ACTIVE
  // status, the Merchant tab is hoisted to the top so the practice
  // can't miss it. ACTIVE merchants keep the legacy 4th-row position.
  // AR Aging shows in the sidebar only for practices where it's enabled
  // (features.arAging comes from /api/payments/enabled-practices, derived from
  // ar_aging_config). The endpoint still 403s for non-enabled practices, so
  // the tab and its deep-link only ever return data for enabled ones.
  const arAgingEnabled = !!practices?.find(
    (p) => p.practiceId === practiceId
  )?.features?.arAging;
  // Same pattern for General Reports (general_reports_config on the backend).
  const generalReportsEnabled = !!practices?.find(
    (p) => p.practiceId === practiceId
  )?.features?.generalReports;
  // Same pattern for the Schedule tab (visit_schedule_config on the backend).
  const visitScheduleEnabled = !!practices?.find(
    (p) => p.practiceId === practiceId
  )?.features?.visitSchedule;

  // ?tab=report is the Report tab, which only some practices have. Everyone
  // else (including old Reconciliation bookmarks, which used ?tab=report)
  // lands on Reconciliation — the Report API 403s for them, which would
  // otherwise bounce the user to login.
  useEffect(() => {
    if (activeTab === 'report' && practicesLoaded && !generalReportsEnabled) {
      setActiveTab('reconciliation');
    }
    if (activeTab === 'schedule' && practicesLoaded && !visitScheduleEnabled) {
      setActiveTab('invoice');
    }
  }, [activeTab, practicesLoaded, generalReportsEnabled, visitScheduleEnabled]);

  const tabs = (() => {
    // Sidebar icons come from components/Icon, keyed by tab id.
    const base = [
      { id: 'ledger', label: 'Ledger' },
      ...(arAgingEnabled
        ? [{ id: 'ar-aging', label: 'AR Aging' }]
        : []),
      { id: 'invoice', label: 'Invoices' },
      ...(visitScheduleEnabled
        ? [{ id: 'schedule', label: 'Schedule' }]
        : []),
      { id: 'payment-plans', label: 'Payment Plans' },
      { id: 'notifications', label: 'Notifications' },
      { id: 'merchant', label: 'Merchant' },
      { id: 'reminders', label: 'Reminders' },
      // Hidden for now — the routes (and renderTabContent cases) are
      // still wired up so deep-links + tab=transactions/claims still
      // work, just removed from the sidebar to declutter.
      // { id: 'transactions', label: 'Transactions' },
      // { id: 'claims', label: 'Claims' },
      { id: 'reconciliation', label: 'Reconciliation' },
      // The DI-style daily/weekly/monthly report.
      ...(generalReportsEnabled
        ? [{ id: 'report', label: 'Report' }]
        : []),
      { id: 'payment-report', label: 'Payment Report' },
      { id: 'deposit-report', label: 'Deposit Report' },
      { id: 'refund-details', label: 'Refund Details' },
      { id: 'chargeback-details', label: 'Chargeback Details' },
      { id: 'device-management', label: 'Device Management' },
      // Hidden for now — re-enable when the practice-level toggles ship.
      // { id: 'settings', label: 'Settings' },
    ];
    if (isMerchantActive === false) {
      const merchant = base.find((t) => t.id === 'merchant');
      const rest = base.filter((t) => t.id !== 'merchant');
      return [merchant, ...rest];
    }
    return base;
  })();

  const handleLogout = () => {
    // Clear localStorage
    localStorage.clear();
    // Redirect to login
    window.location.href = '/login';
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'merchant':
        return <MerchantTab practiceId={practiceId} />;
      case 'device-management':
        return <DeviceManagementTab practiceId={practiceId} />;
      case 'invoice':
        return (
          <InvoiceTab
            practiceId={practiceId}
            chargeRequest={chargeRequest}
            onChargeHandled={() => setChargeRequest(null)}
          />
        );
      case 'schedule':
        // Blank until we know the practice has it; the API 403s otherwise.
        return visitScheduleEnabled ? <ScheduleTab practiceId={practiceId} /> : null;
      case 'payment-plans':
        return <PaymentPlansTab practiceId={practiceId} />;
      case 'notifications':
        return <NotificationsTab practiceId={practiceId} />;
      case 'ledger':
        return <LedgerTab practiceId={practiceId} onRequestCharge={requestCharge} />;
      case 'ar-aging':
        return <ARAgingTab practiceId={practiceId} />;
      case 'report':
        // Blank until we know the practice has it (see the redirect above).
        return generalReportsEnabled ? <GeneralReportsTab practiceId={practiceId} /> : null;
      case 'transactions':
        return <TransactionsTab practiceId={practiceId} />;
      case 'claims':
        return <ClaimsTab practiceId={practiceId} />;
      case 'reconciliation':
        return <ReportTab practiceId={practiceId} />;
      case 'payment-report':
        return <PaymentReportTab practiceId={practiceId} />;
      case 'deposit-report':
        return <DepositReportTab practiceId={practiceId} />;
      case 'refund-details':
        return <RefundDetailsTab practiceId={practiceId} />;
      case 'chargeback-details':
        return <ChargebackDetailsTab practiceId={practiceId} />;
      case 'reminders':
        return <RemindersTab practiceId={practiceId} />;
      case 'settings':
        return <SettingsTab practiceId={practiceId} />;
      default:
        return <LedgerTab practiceId={practiceId} onRequestCharge={requestCharge} />;
    }
  };

  const activeMeta = TAB_META[activeTab];

  return (
    <div className="pm-app" style={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      <aside className="pm-sidebar">
        <div className="pm-sidebar-header">
          <div className="pm-brand">
            <img src={newtonWordmark} alt="Newton" />
            <span className="pm-brand-divider" aria-hidden="true" />
            <span className="pm-brand-label">Pay</span>
          </div>
          <PracticePicker
            practices={practices}
            value={practiceId}
            onChange={handlePracticeChange}
          />
          {/* Blurred with Hide PHI on; the hover tooltip is dropped too so
              it can't reveal the address. */}
          <p className="pm-user-email" data-phi title={phiHidden ? undefined : userEmail || undefined}>
            {userEmail}
          </p>
          <KollaSyncIndicator practiceId={practiceId} />
        </div>

        {/* Navigation Tabs */}
        <nav className="pm-nav" aria-label="Payments">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                className="pm-nav-item"
                aria-current={isActive ? 'page' : undefined}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon name={tab.id} active={isActive} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Hide PHI + Logout */}
        <div className="pm-sidebar-footer">
          <button
            type="button"
            className="pm-nav-item pm-phi-toggle"
            role="switch"
            aria-checked={phiHidden}
            onClick={() => setPhiHidden((v) => !v)}
            title={phiHidden
              ? 'Patient details are blurred. Click to show them.'
              : 'Blur patient names, contact details and IDs — for screen-sharing and demos'}
          >
            <Icon name={phiHidden ? 'eye-off' : 'eye'} />
            <span>Hide PHI</span>
            <span className="pm-switch" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="pm-nav-item pm-logout"
            onClick={handleLogout}
          >
            <Icon name="logout" />
            <span>Log out</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="pm-main">
        {activeMeta && (
          <header className="pm-page-header">
            <h1 className="pm-page-title">{activeMeta.title}</h1>
            <p className="pm-page-subtitle">{activeMeta.description}</p>
          </header>
        )}

        {/* Tab Content */}
        <div key={practiceId}>
          {renderTabContent()}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;

